import { z } from 'zod/v4';
import { EntityHistoryEntrySchema } from './entry.js';
import {
  buildEntityHistoryListResponseSchema,
  EntityHistoryListQuerySchema,
} from './list.js';

/** Sized for the widest column a filter compares against (`varchar(64)`). */
const FilterValue = z.string().trim().min(1).max(64);

/**
 * The staff-wide history feed: every entry, newest first, keyset-paginated
 * like every other history read. Each filter is an exact match on one column.
 *
 * What each filter costs — see `listEntityHistoryAdminDb`:
 * - `entityType` (+ `entityId`) is a prefix of the entity index.
 * - `companyId` (`for_company_id`) has its own `(for_company_id, id)` index.
 * - `from` / `to` become a primary-key range, since the id is a uuid v7 of
 *   `created_at`.
 * - The rest are post-filters on one of those scans.
 */
export const AdminEntityHistoryListQuerySchema =
  EntityHistoryListQuerySchema.extend({
    entityType: FilterValue.optional(),
    entityId: FilterValue.optional(),
    entitySubType: FilterValue.optional(),
    eventType: FilterValue.optional(),
    source: FilterValue.optional(),
    /** The company the entity belongs to (`for_company_id`), not the actor's. */
    companyId: FilterValue.optional(),
    /** The user who made the change (`created_by_user_id`). */
    actorUserId: FilterValue.optional(),
    correlationId: FilterValue.optional(),
    /** Inclusive lower bound on `createdAt`. */
    from: z.iso.datetime({ offset: true }).optional(),
    /** Exclusive upper bound on `createdAt`. */
    to: z.iso.datetime({ offset: true }).optional(),
  });

export type AdminEntityHistoryListQuery = z.infer<
  typeof AdminEntityHistoryListQuerySchema
>;

export const AdminEntityHistoryParamsSchema = z.object({
  id: z.uuid(),
});

/**
 * An entry with the names of its three ownership companies, so a reader can
 * tell whose record it is without looking each id up. A name is null when
 * the company has no self ABOUT profile (a removed company, say).
 */
export const AdminEntityHistoryEntrySchema = EntityHistoryEntrySchema.extend({
  forCompanyName: z.string().nullable(),
  byCompanyName: z.string().nullable(),
  onBehalfOfCompanyName: z.string().nullable(),
});

export type AdminEntityHistoryEntry = z.infer<
  typeof AdminEntityHistoryEntrySchema
>;

export const AdminEntityHistoryListResponseSchema =
  buildEntityHistoryListResponseSchema(AdminEntityHistoryEntrySchema);
